import React, { useState } from 'react';
import { Area, AreaChart, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from '@/components/ui/chart';
import { cn } from '@/lib/utils';

export interface DemoChartDataPoint {
  date: string;
  total: number;
  qualified: number;
  booked: number;
  notBooked: number;
}

interface SessionsChartProps {
  data: DemoChartDataPoint[];
  className?: string;
}

type TimePeriod = '7d' | '1m' | '3m' | '6m' | '1y';

const chartConfig = {
  total: {
    label: 'All Sessions',
    color: 'hsl(210 85% 60%)',
  },
  qualified: {
    label: 'Qualified',
    color: 'hsl(280 65% 60%)',
  },
} satisfies ChartConfig;

const filterDataByPeriod = (data: DemoChartDataPoint[], period: TimePeriod): DemoChartDataPoint[] => {
  if (!data || data.length === 0) return [];

  const now = new Date();
  let daysBack: number;

  switch (period) {
    case '7d':
      daysBack = 7;
      break;
    case '1m':
      daysBack = 30;
      break;
    case '3m':
      daysBack = 90;
      break;
    case '6m':
      daysBack = 180;
      break;
    case '1y':
      daysBack = 365;
      break;
    default:
      daysBack = 30;
  }

  const cutoffDate = new Date(now.getTime() - daysBack * 24 * 60 * 60 * 1000);

  return data.filter(point => {
    const pointDate = new Date(point.date);
    return pointDate >= cutoffDate;
  });
};

export const SessionsChart: React.FC<SessionsChartProps> = ({ data, className }) => {
  const [period, setPeriod] = useState<TimePeriod>('1m');

  const filteredData = filterDataByPeriod(data, period);

  if (!data || data.length === 0) {
    return (
      <Card className={cn('card-bg card-border', className)}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold text-heading">Sessions Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex h-[300px] items-center justify-center text-subtle">
            No session data yet.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('card-bg card-border', className)}>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-base font-semibold text-heading">Timeline</CardTitle>
        <Tabs value={period} onValueChange={(v) => setPeriod(v as TimePeriod)}>
          <TabsList className="h-8 card-secondary-bg border border-default">
            {(['7d', '1m', '3m', '6m', '1y'] as TimePeriod[]).map((p) => (
              <TabsTrigger
                key={p}
                value={p}
                className={cn(
                  'text-xs px-2.5 h-6',
                  'data-[state=active]:bg-card data-[state=active]:shadow-sm',
                  'data-[state=active]:text-heading',
                  'text-muted-foreground'
                )}
              >
                {p === '7d' ? '7d' : p === '1m' ? '1m' : p === '3m' ? '3m' : p === '6m' ? '6m' : '1y'}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </CardHeader>
      <CardContent className="pt-4">
        <ChartContainer config={chartConfig} className="aspect-auto h-[280px] w-full">
          <AreaChart data={filteredData} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="fillTotal" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-total)" stopOpacity={0.3} />
                <stop offset="95%" stopColor="var(--color-total)" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="fillQualified" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-qualified)" stopOpacity={0.3} />
                <stop offset="95%" stopColor="var(--color-qualified)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-[hsl(var(--border))]" />
            <YAxis
              axisLine={false}
              tickLine={false}
              tickMargin={8}
              tickFormatter={(value) => value.toLocaleString()}
              domain={[0, 'auto']}
              width={40}
              className="text-subtle"
              tick={{ fontSize: 12 }}
            />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={40}
              className="text-subtle"
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => {
                try {
                  const date = new Date(value);
                  if (isNaN(date.getTime())) return '--';
                  return date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  });
                } catch {
                  return '--';
                }
              }}
            />
            <ChartTooltip
              cursor={{ strokeWidth: 1, className: 'stroke-[hsl(var(--border))]' }}
              content={
                <ChartTooltipContent
                  className="w-[180px] card-bg border border-default shadow-lg"
                  labelFormatter={(value) => {
                    try {
                      const date = new Date(value);
                      if (isNaN(date.getTime())) return 'Invalid Date';
                      return date.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      });
                    } catch {
                      return 'Invalid Date';
                    }
                  }}
                  indicator="dot"
                />
              }
            />
            <Area
              dataKey="total"
              type="monotone"
              fill="url(#fillTotal)"
              stroke="var(--color-total)"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2 }}
            />
            <Area
              dataKey="qualified"
              type="monotone"
              fill="url(#fillQualified)"
              stroke="var(--color-qualified)"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2 }}
            />
            <ChartLegend content={<ChartLegendContent />} />
          </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};
